// The repository index (`/gh`; docs/design.md, "Repository index"): which
// repositories it lists, in what order, what its filter keeps, and how
// GitHub's answers about them read. Pure: the store
// (state/repos.svelte.ts) does the reading.
//
// The order:
//   1. pinned (this browser's pins, the jump bar's places, in the order pinned);
//   2. those with pull requests waiting on the viewer (the dashboard's
//      saved rows), most waiting first;
//   3. those the viewer pushed to recently (their public events), latest
//      push first;
//   4. the rest by their last push, the unknown last.
import { isSamlError, type GraphQLError } from '../api/graphqlError';
import { sameLogin } from './attention';

/** A repository's key: `owner/name`, lowercased (GitHub's names are case-insensitive). */
export const repoKey = (owner: string, name: string) => `${owner}/${name}`.toLowerCase();

/** A repository as named. */
export interface RepoName {
  owner: string;
  name: string;
}

/** A repository the index lists. */
export interface RepoEntry extends RepoName {
  key: string;
  /** GitHub's ID of it (`databaseId`, REST `id`), which a rename keeps; absent when the listing did not say. */
  id?: number | null;
  /** When anyone last pushed to it (ISO), as the listing said; null when unknown. */
  pushedAt: string | null;
}

/** The index's groups, in order. */
export type RepoGroup = 'pinned' | 'waiting' | 'pushed' | 'rest';

/** A repository in the index's order, with why it is where it is. */
export interface RepoRow extends RepoEntry {
  group: RepoGroup;
  /** Open pull requests waiting on the viewer here, as the dashboard last read them. */
  waiting: number;
  /** When the viewer last pushed here, within the events read; null when not. */
  viewerPushedAt: string | null;
  pinned: boolean;
}

/** What orders the index besides the listing. */
export interface RepoSignals {
  /** Pinned keys, in the order pinned. */
  pins: readonly string[];
  /** Pull requests waiting on the viewer, by key. */
  waiting: ReadonlyMap<string, number>;
  /** The viewer's latest push (ISO), by key. */
  pushed: ReadonlyMap<string, string>;
  /** How a repository the listing lacks is spelled, by key, where something named it. */
  names?: ReadonlyMap<string, RepoName>;
  /** GitHub's ID of a repository a signal names, by the key it named it under: a rename since finds it listed under its new name. */
  ids?: ReadonlyMap<string, number>;
}

const byText = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
/** Newer first, the unknown last. */
const newer = (a: string | null, b: string | null) => (a === b ? 0 : a === null ? 1 : b === null ? -1 : byText(b, a));

/**
 * The index: every repository `entries` lists, and those only the signals
 * name (a pin, a pull request waiting, a push), in the index's order.
 */
export function orderRepos(entries: readonly RepoEntry[], signals: RepoSignals): RepoRow[] {
  const all = new Map<string, RepoEntry>();
  const byId = new Map<number, string>();
  for (const e of entries)
    if (!all.has(e.key)) {
      all.set(e.key, e);
      if (typeof e.id === 'number') byId.set(e.id, e.key);
    }
  // A push under a name the repository no longer has counts under the name the listing has.
  const pushed = new Map<string, string>();
  for (const [key, at] of signals.pushed) {
    const id = signals.ids?.get(key);
    const to = id !== undefined && byId.has(id) ? byId.get(id)! : key;
    const was = pushed.get(to);
    if (!was || was < at) pushed.set(to, at);
  }
  for (const key of [...signals.pins, ...signals.waiting.keys(), ...pushed.keys()]) {
    if (all.has(key)) continue;
    const named = signals.names?.get(key);
    const [owner, name] = key.split('/');
    all.set(key, { key, owner: named?.owner ?? owner, name: named?.name ?? name, pushedAt: null });
  }
  const pinAt = new Map(signals.pins.map((k, i) => [k, i]));
  const rows = [...all.values()].map((e): RepoRow => {
    const waiting = signals.waiting.get(e.key) ?? 0;
    const viewerPushedAt = pushed.get(e.key) ?? null;
    const pinned = pinAt.has(e.key);
    const group: RepoGroup = pinned ? 'pinned' : waiting > 0 ? 'waiting' : viewerPushedAt ? 'pushed' : 'rest';
    return { ...e, group, waiting, viewerPushedAt, pinned };
  });
  const rank: Record<RepoGroup, number> = { pinned: 0, waiting: 1, pushed: 2, rest: 3 };
  return rows.sort(
    (a, b) =>
      rank[a.group] - rank[b.group] ||
      (a.group === 'pinned' ? pinAt.get(a.key)! - pinAt.get(b.key)! : 0) ||
      (a.group === 'waiting' ? b.waiting - a.waiting : 0) ||
      (a.group === 'pushed' ? newer(a.viewerPushedAt, b.viewerPushedAt) : 0) ||
      newer(a.pushedAt, b.pushedAt) ||
      byText(a.key, b.key),
  );
}

/** What the index's controls keep: words matched against `owner/name`, and one owner. */
export interface RepoQuery {
  text?: string;
  owner?: string | null;
}

/**
 * The rows `q` keeps, at most `limit`: each word of `text` is in
 * `owner/name`, and the owner is `owner`, ignoring case. They are ranked
 * by how `text` (trimmed) meets them: the name or `owner/name` exactly,
 * then one of them starting with it, then the rest, each rank in the
 * rows' own order. Without text, the rows' order.
 */
export function filterRepos<R extends RepoEntry>(rows: readonly R[], q: RepoQuery, limit = Infinity): R[] {
  const text = (q.text ?? '').trim().toLowerCase();
  const words = text.split(/\s+/).filter(Boolean);
  const owner = q.owner?.toLowerCase() || null;
  const kept = rows.filter((r) => (owner === null || r.owner.toLowerCase() === owner) && words.every((w) => r.key.includes(w)));
  if (!text) return kept.slice(0, limit);
  const rank = (r: R) => {
    const name = r.name.toLowerCase();
    return name === text || r.key === text ? 0 : name.startsWith(text) || r.key.startsWith(text) ? 1 : 2;
  };
  return kept
    .map((r, i) => ({ r, i, k: rank(r) }))
    .sort((a, b) => a.k - b.k || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.r);
}

/** The owners of `rows`, with how many each has, most first (then by name): the owner chips. */
export function ownersOf(rows: readonly RepoEntry[]): { login: string; count: number }[] {
  const by = new Map<string, { login: string; count: number }>();
  for (const r of rows) {
    const k = r.owner.toLowerCase();
    const o = by.get(k) ?? { login: r.owner, count: 0 };
    o.count++;
    by.set(k, o);
  }
  return [...by.values()].sort((a, b) => b.count - a.count || byText(a.login.toLowerCase(), b.login.toLowerCase()));
}

// ---------------------------------------------------------------- what orders it

/** How far back a push counts as recent: 30 days (GitHub keeps 90 days of events, at most 300 of them). */
export const RECENT_PUSH_MS = 30 * 86_400_000;

/**
 * The viewer's latest push to each repository among `events` (GitHub's
 * `users/{login}/events`), within RECENT_PUSH_MS of `now`, and how each
 * repository is spelled.
 */
export function pushesFrom(events: readonly unknown[], now: number): { pushed: Map<string, string>; names: Map<string, RepoName>; ids: Map<string, number> } {
  const pushed = new Map<string, string>();
  const names = new Map<string, RepoName>();
  const ids = new Map<string, number>();
  for (const e of events) {
    if (typeof e !== 'object' || e === null) continue;
    const { type, repo, created_at } = e as { type?: unknown; repo?: { id?: unknown; name?: unknown } | null; created_at?: unknown };
    if (type !== 'PushEvent' || typeof repo?.name !== 'string' || typeof created_at !== 'string') continue;
    const at = Date.parse(created_at);
    const [owner, name, extra] = repo.name.split('/');
    if (!owner || !name || extra !== undefined || Number.isNaN(at) || now - at > RECENT_PUSH_MS) continue;
    const key = repoKey(owner, name);
    const was = pushed.get(key);
    if (!was || was < created_at) pushed.set(key, created_at);
    if (!names.has(key)) names.set(key, { owner, name });
    if (typeof repo.id === 'number') ids.set(key, repo.id);
  }
  return { pushed, names, ids };
}

/** The dashboard's saved rows, as much of them as the index reads. */
export interface SavedTurns {
  prs: readonly { id: string; state: string; repository: { nameWithOwner: string } }[];
  /** Each pull request's attention set, by node ID, where it was known. */
  attention: Readonly<Record<string, readonly { login: string }[]>>;
}

/** How many open pull requests wait on `me` in each repository (their attention names them), and how each is spelled. */
export function waitingFrom(saved: SavedTurns, me: string): { waiting: Map<string, number>; names: Map<string, RepoName> } {
  const waiting = new Map<string, number>();
  const names = new Map<string, RepoName>();
  const seen = new Set<string>();
  for (const p of saved.prs) {
    if (seen.has(p.id) || p.state !== 'OPEN') continue;
    seen.add(p.id);
    if (!saved.attention[p.id]?.some((m) => sameLogin(m.login, me))) continue;
    const [owner, name] = p.repository.nameWithOwner.split('/');
    if (!owner || !name) continue;
    const key = repoKey(owner, name);
    waiting.set(key, (waiting.get(key) ?? 0) + 1);
    if (!names.has(key)) names.set(key, { owner, name });
  }
  return { waiting, names };
}

// ---------------------------------------------------------------- GitHub's answers

/** The branches read per repository, most recently committed first: "yours without a pull request" counts among these. */
export const BRANCHES_READ = 50;

/** Repositories per counts request, one alias each. */
export const COUNTS_BATCH = 10;

/**
 * One request for the counts of `n` repositories, one alias each (`r0`…),
 * named by the variables `o<i>` and `n<i>`: its open pull requests, its
 * default branch's checks, and its BRANCHES_READ most recently committed
 * branches with their tips' author and committer and their open pull
 * requests. GitHub's cost: about BRANCHES_READ + 2 connections a
 * repository, so a full batch is about 5 points.
 */
export function repoCountsQuery(n: number): string {
  const vars = Array.from({ length: n }, (_, i) => `$o${i}: String!, $n${i}: String!`).join(', ');
  const fields = Array.from(
    { length: n },
    (_, i) =>
      `r${i}: repository(owner: $o${i}, name: $n${i}) {
    nameWithOwner isPrivate viewerPermission pushedAt
    pullRequests(states: OPEN) { totalCount }
    defaultBranchRef { name target { ... on Commit { statusCheckRollup { state } } } }
    refs(refPrefix: "refs/heads/", first: ${BRANCHES_READ}, orderBy: { field: TAG_COMMIT_DATE, direction: DESC }) {
      totalCount
      nodes { name target { ... on Commit { committedDate author { user { login } } committer { user { login } } } } associatedPullRequests(states: OPEN) { totalCount } }
    }
  }`,
  ).join('\n  ');
  return `query RepoIndexCounts(${vars}) {\n  ${fields}\n}`;
}

/** The variables of `repoCountsQuery(repos.length)`. */
export function repoCountsVars(repos: readonly RepoName[]): Record<string, string> {
  const vars: Record<string, string> = {};
  repos.forEach((r, i) => {
    vars['o' + i] = r.owner;
    vars['n' + i] = r.name;
  });
  return vars;
}

/** A default branch's checks, as a dot: passed, failed, under way; null with none. */
export type ChecksDot = 'good' | 'bad' | 'pending' | null;

/** What a row shows once its counts load. */
export interface RepoCounts {
  /** As GitHub spells it. */
  nameWithOwner: string;
  isPrivate: boolean;
  viewerPermission: string | null;
  pushedAt: string | null;
  openPulls: number;
  /** The viewer's branches (their tip's author or committer is the viewer) with no open pull request, but the default branch, among those read. */
  myBranches: string[];
  /** The repository has more branches than were read: `myBranches` may miss older ones. */
  branchesCapped: boolean;
  defaultBranch: string | null;
  checks: ChecksDot;
}

const dotOf = (state: unknown): ChecksDot =>
  state === 'SUCCESS' ? 'good' : state === 'FAILURE' || state === 'ERROR' ? 'bad' : typeof state === 'string' ? 'pending' : null;

interface RefNode {
  name?: string;
  target?: { author?: { user?: { login?: string } | null } | null; committer?: { user?: { login?: string } | null } | null } | null;
  associatedPullRequests?: { totalCount?: number } | null;
}

interface RepoNode {
  nameWithOwner?: string;
  isPrivate?: boolean;
  viewerPermission?: string | null;
  pushedAt?: string | null;
  pullRequests?: { totalCount?: number } | null;
  defaultBranchRef?: { name?: string; target?: { statusCheckRollup?: { state?: string } | null } | null } | null;
  refs?: { totalCount?: number; nodes?: (RefNode | null)[] } | null;
}

/**
 * What one alias of `repoCountsQuery` answered:
 * - `ready`: its counts;
 * - `missing`: GitHub does not show the viewer the repository (null with
 *   NOT_FOUND, or with no error);
 * - `sso`: reading it needs the viewer to authorize their SSO session for
 *   its organization (SAML enforcement);
 * - `forbidden`: GitHub refused it otherwise (an IP allow list, say), in
 *   GitHub's words;
 * - `failed`: GitHub failed or ran out of time on it: worth trying again.
 */
export type CountRead =
  | { kind: 'ready'; counts: RepoCounts }
  | { kind: 'missing' }
  | { kind: 'sso'; message: string }
  | { kind: 'forbidden'; message: string }
  | { kind: 'failed'; errors: GraphQLError[] };

/** Why an alias came back null, from the errors whose path starts at it. */
function nullRead(errors: readonly GraphQLError[]): CountRead {
  const saml = errors.find(isSamlError);
  if (saml) return { kind: 'sso', message: saml.message };
  const refused = errors.find((e) => e.type === 'FORBIDDEN');
  if (refused) return { kind: 'forbidden', message: refused.message };
  const other = errors.filter((e) => e.type !== 'NOT_FOUND');
  return other.length ? { kind: 'failed', errors: other } : { kind: 'missing' };
}

/**
 * Each of `repos`'s counts from `repoCountsQuery`'s answer (`data`, and
 * the `errors` GitHub gave beside it), by key: see `CountRead`. An error
 * deeper inside an alias that answered costs it nothing.
 */
export function readCounts(data: unknown, errors: readonly GraphQLError[], repos: readonly RepoName[], me: string): Map<string, CountRead> {
  const out = new Map<string, CountRead>();
  const root = (data ?? {}) as Record<string, RepoNode | null | undefined>;
  repos.forEach((r, i) => {
    const node = root['r' + i];
    const key = repoKey(r.owner, r.name);
    if (!node || typeof node.nameWithOwner !== 'string') return void out.set(key, nullRead(errors.filter((e) => e.path?.[0] === 'r' + i)));
    const dflt = node.defaultBranchRef?.name ?? null;
    const refs = (node.refs?.nodes ?? []).filter((n): n is RefNode => !!n && typeof n.name === 'string');
    const mine = (n: RefNode) => [n.target?.author?.user?.login, n.target?.committer?.user?.login].some((l) => !!l && sameLogin(l, me));
    const counts: RepoCounts = {
      nameWithOwner: node.nameWithOwner,
      isPrivate: !!node.isPrivate,
      viewerPermission: node.viewerPermission ?? null,
      pushedAt: node.pushedAt ?? null,
      openPulls: node.pullRequests?.totalCount ?? 0,
      myBranches: refs.filter((n) => n.name !== dflt && !n.associatedPullRequests?.totalCount && mine(n)).map((n) => n.name!),
      branchesCapped: (node.refs?.totalCount ?? 0) > refs.length,
      defaultBranch: dflt,
      checks: dotOf(node.defaultBranchRef?.target?.statusCheckRollup?.state),
    };
    out.set(key, { kind: 'ready', counts });
  });
  return out;
}

/** Repositories per page of the viewer's repositories. */
export const VIEWER_REPOS_PAGE = 100;
/** Pages of the viewer's repositories read at most. */
export const VIEWER_REPOS_PAGES = 5;

/**
 * The viewer's repositories, where installations cannot be listed (a
 * development token sign-in): those they own, collaborate on, or reach as
 * an organization's member, most recently pushed first. One point a page.
 */
export const VIEWER_REPOS_QUERY = `
query ViewerRepositories($after: String) {
  viewer {
    repositories(first: ${VIEWER_REPOS_PAGE}, after: $after, affiliations: [OWNER, COLLABORATOR, ORGANIZATION_MEMBER], ownerAffiliations: [OWNER, COLLABORATOR, ORGANIZATION_MEMBER], orderBy: { field: PUSHED_AT, direction: DESC }) {
      pageInfo { hasNextPage endCursor }
      nodes { databaseId name owner { login } pushedAt }
    }
  }
}`;

/** A page of `VIEWER_REPOS_QUERY`: its repositories and the cursor of the next, or null at the end. */
export function readViewerRepos(data: unknown): { entries: RepoEntry[]; next: string | null } {
  const conn = (data as { viewer?: { repositories?: { pageInfo?: { hasNextPage?: boolean; endCursor?: string | null }; nodes?: unknown[] } } } | null)?.viewer?.repositories;
  const entries: RepoEntry[] = [];
  for (const n of conn?.nodes ?? []) {
    const r = n as { databaseId?: unknown; name?: unknown; owner?: { login?: unknown } | null; pushedAt?: unknown } | null;
    if (!r || typeof r.name !== 'string' || typeof r.owner?.login !== 'string') continue;
    entries.push({ key: repoKey(r.owner.login, r.name), ...(typeof r.databaseId === 'number' ? { id: r.databaseId } : {}), owner: r.owner.login, name: r.name, pushedAt: typeof r.pushedAt === 'string' ? r.pushedAt : null });
  }
  return { entries, next: conn?.pageInfo?.hasNextPage && conn.pageInfo.endCursor ? conn.pageInfo.endCursor : null };
}
